
import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";
import type { SupportedLanguage } from "./languageUtils";

// Type for Survey from database
export type Survey = Database["public"]["Tables"]["surveys"]["Row"];

// Survey IDs for different survey types
export const SURVEY_IDS = {
  // First survey (visit 1)
  FIRST_SURVEY_ID: "c3d4e5f6-a7b8-9012-3456-cdefab123456", // Background survey
  // Survey for BCAP (shown on all visits)
  BCAP_SURVEY_ID: "b2c3d4e5-f6a7-8901-2345-bcdefa123456", // BCAP survey
  // Survey for Leisure Activities (shown as 2nd and 3rd visits first survey)
  LEISURE_ACTIVITIES_ID: "d4e5f6a7-b8c9-0123-4567-defabc123456", // Leisure survey
  // Survey for Social Services (shown as final survey in each visit)
  SOCIAL_SERVICES_ID: "a1b2c3d4-e5f6-7890-1234-abcdef123456" // Trust survey
};

/**
 * Fetch survey questions from the database for a specific language and survey
 */
export const fetchSurveyQuestions = async (
  language: SupportedLanguage, 
  selectedSurvey: string
): Promise<Survey[]> => {
  try {
    // Determine which survey IDs to retrieve based on selectedSurvey
    let surveyIds = [];
    
    if (selectedSurvey === "1") {
      // First visit: Background + BCAP + Trust
      surveyIds = [
        SURVEY_IDS.FIRST_SURVEY_ID,     // Background
        SURVEY_IDS.BCAP_SURVEY_ID,      // BCAP
        SURVEY_IDS.SOCIAL_SERVICES_ID   // Trust
      ];
    } else if (selectedSurvey === "2" || selectedSurvey === "3") {
      // Second & Third visits: Leisure + BCAP + Trust
      surveyIds = [
        SURVEY_IDS.LEISURE_ACTIVITIES_ID, // Leisure
        SURVEY_IDS.BCAP_SURVEY_ID,        // BCAP
        SURVEY_IDS.SOCIAL_SERVICES_ID     // Trust
      ];
    }
    
    if (surveyIds.length === 0) {
      throw new Error(`Invalid survey selection: ${selectedSurvey}`);
    }
    
    // Fetch all surveys in parallel
    const surveysPromises = surveyIds.map(id => 
      supabase
        .from("surveys")
        .select("*")
        .eq("id", id)
        .single()
    );
    
    const responses = await Promise.all(surveysPromises);
    
    // Check if any fetch failed
    const errors = responses.filter(res => res.error);
    if (errors.length > 0) {
      console.error("Error fetching surveys:", errors);
      throw new Error(`Failed to fetch surveys: ${errors[0].error.message}`);
    }
    
    // Extract data
    return responses.map(res => res.data as Survey).filter(Boolean);
  } catch (error) {
    console.error("Error fetching survey questions:", error);
    throw error;
  }
};
